import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SyncIcon from '@mui/icons-material/Sync';
import VisibilityIcon from '@mui/icons-material/Visibility';
import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Controller, useForm } from 'react-hook-form';

import { dates, labOrderStatus } from '../../constants';
import ExcelIcon from '../../icons/excelIcon';
import { fetchLabOrders } from '../../services/labOrder.service';
import {
    customTableStyles,
    customTableStylesAccountView,
    customizeDateFormat,
    dateTimeFormat,
    showPhenoTypes,
} from '../../utils';
import MessageContainer from '../message.component';
import WithLogin from '../withLogin.component';
import AIQReportLogs from './labOrderAIQReportLogs.component';
import EditLabOrder from './labOrderEdit.component';
import ViewLabOrder from './labOrderView.component';
import ViewExportLabOrderForm from './viewExportLabOrderForm.component';

const LabOrderList = props => {
    const [data, setData] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(25);
    const [page, setPage] = useState(1);
    const [openAIQModel, setOpenAIQModel] = useState(false);
    const [openEditForm, setOpenEditForm] = useState(false);
    const [openViewForm, setOpenViewForm] = useState(false);
    const [openExportForm, setOpenExportForm] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const [message, setMessage] = useState({});
    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();
    const showFilters = props.showFilters == null ? true : false;

    const [labOrderSearchFilters, setLabOrderSearchFilters] = useState({
        searchText: '',
        accountId: props.accountId,
        currentStatus: '',
        startDate: '',
        endDate: '',
    });

    const columns = [
        {
            name: 'ID',
            expander: true,
            selector: row => row.id,
        },
        {
            name: 'Site',
            expander: true,
            selector: row => row.clinic_name,
        },
        {
            name: 'Provider Name',
            selector: row =>
                `${row.provider.account_person.first_name} ${row.provider.account_person.last_name}`,
        },
        {
            name: 'Subject ID',
            selector: row => row.patient.subject_id,
        },
        {
            name: 'Sample Id',
            selector: row => {
                if (
                    row.lab_status &&
                    row.lab_status.toUpperCase() ===
                        'sample on hold'.toUpperCase()
                ) {
                    return (
                        <>
                            {row.sample_id}
                            <PauseCircleIcon
                                style={{ color: 'orange' }}
                                fontSize="large"
                                titleAccess="Sample processing hold"
                            />
                        </>
                    );
                } else if (
                    row.lab_status &&
                    row.lab_status.toUpperCase() ===
                        'sample voided'.toUpperCase()
                ) {
                    return (
                        <>
                            {row.sample_id}
                            <BlockIcon
                                style={{ color: 'orange' }}
                                fontSize="large"
                                titleAccess="Sample voided"
                            />
                        </>
                    );
                } else if (
                    row.lab_status &&
                    row.lab_status.toUpperCase() === 'sample lost'.toUpperCase()
                ) {
                    return (
                        <>
                            {row.sample_id}
                            <ReportProblemIcon
                                style={{ color: 'orange' }}
                                fontSize="large"
                                titleAccess="Sample lost"
                            />
                        </>
                    );
                } else {
                    return row.sample_id;
                }
            },
            minWidth: '10%',
        },
        {
            name: 'Client Id',
            selector: row => row.client_sample_id,
        },
        {
            name: 'Status',
            selector: row => (row.mode === 'Draft' ? 'Draft' : row.status),
        },

        {
            name: 'Result',
            cell: row => {
                return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {row.pheno_types && showPhenoTypes(row.pheno_types)}
                    </div>
                );
            },
        },
        {
            name: 'BMI',
            selector: row => row.bmi,
        },
        {
            name: 'Created at',
            selector: row => dateTimeFormat(row.created_at),
        },
        {
            key: 'action',
            text: 'Action',
            name: 'Action',
            className: 'action',
            align: 'left',
            sortable: false,
            cell: row => {
                return (
                    <>
                        {!row.report?.is_published_provider && (
                            <>
                                <span
                                    className="table-action-icon"
                                    title="Edit">
                                    <EditIcon
                                        className="icon-color"
                                        onClick={() => {
                                            onEdit(row);
                                        }}
                                    />
                                </span>
                                &nbsp;
                            </>
                        )}
                        <span className="table-action-icon" title="Details">
                            <VisibilityIcon
                                className="icon-color"
                                onClick={() => {
                                    onView(row);
                                }}
                            />
                        </span>
                        <>&nbsp;</>

                        {row.is_result_ready && (
                            <>
                                <span
                                    className="table-action-icon"
                                    title="Run AIQ">
                                    <PsychologyIcon
                                        className="icon-color"
                                        onClick={() => {
                                            setOpenAIQModel(true);
                                            setSelectedRow(row);
                                        }}
                                    />
                                </span>
                                &nbsp;
                            </>
                        )}
                    </>
                );
            },
        },
    ];

    const conditionalRowStyles = [
        {
            when: row => row.status === 'AIQ Job - Success',
            style: {
                backgroundColor: '#e4e5e6',
                '&:hover': {
                    cursor: 'pointer',
                },
            },
        },
        {
            when: row => row.status.toUpperCase().includes('FAIL'),
            style: {
                backgroundColor: '#FFA500',
            },
        },
        {
            when: row => row.status.toUpperCase().includes('CANCEL'),
            style: {
                backgroundColor: '#ff0404',
            },
        },
        {
            when: row =>
                row.lab_status &&
                (row.lab_status.toUpperCase() ===
                    'sample voided'.toUpperCase() ||
                    row.lab_status.toUpperCase() ===
                        'sample lost'.toUpperCase()),
            style: {
                backgroundColor: '#888484',
            },
        },
    ];

    const onEdit = row => {
        setOpenEditForm(true);
        setSelectedRow(row);
    };
    const onView = row => {
        setOpenViewForm(true);
        setSelectedRow(row);
    };

    const getLabOrders = async (toPage, toPerPage) => {
        setTableLoading(true);
        setMessage({});
        toPage = toPage ? toPage : page;
        toPerPage = toPerPage ? toPerPage : perPage;
        try {
            const response = await fetchLabOrders(
                toPage,
                toPerPage,
                labOrderSearchFilters,
            );
            setData(response.items);
            setTotalRows(response.total);
        } catch (error) {
            setMessage({
                detail: 'An error has occurrded during data loading.',
                type: 'Error',
            });
        }
        setTableLoading(false);
    };

    const handlePageChange = toPage => {
        getLabOrders(toPage);
        setPage(toPage);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        getLabOrders(page, newPerPage);
        setPerPage(newPerPage);
    };

    const reloadDataTable = toPage => {
        toPage = toPage ? toPage : page;
        getLabOrders(toPage);
        if (toPage) {
            setPage(toPage);
        }
    };

    const { control, setValue, getValues, register, handleSubmit, reset } =
        useForm({
            defaultValues: {},
        });

    useEffect(() => {
        setLoading(true);
        getLabOrders(1);
        setPage(1);
        setLoading(false);
    }, [labOrderSearchFilters]); // eslint-disable-line react-hooks/exhaustive-deps

    const setSearchFilters = async data => {
        setLabOrderSearchFilters({
            searchText: data.searchText.trim(),
            accountId: data.accountId ? data.accountId : props.accountId,
            currentStatus: data.currentStatus,
            startDate: startDate,
            endDate: endDate,
        });
    };

    return (
        <>
            {openEditForm && (
                <EditLabOrder
                    open={openEditForm}
                    setEditForm={setOpenEditForm}
                    selectedRow={selectedRow}
                    reloadDataTable={reloadDataTable}
                />
            )}
            {openViewForm && (
                <ViewLabOrder
                    open={openViewForm}
                    setEditForm={setOpenViewForm}
                    selectedRow={selectedRow}
                    reloadDataTable={reloadDataTable}
                />
            )}

            {openExportForm && (
                <ViewExportLabOrderForm
                    open={openExportForm}
                    setEditForm={setOpenExportForm}
                    filter={labOrderSearchFilters}
                />
            )}
            {openAIQModel && (
                <AIQReportLogs
                    open={openAIQModel}
                    setOpenAIQModel={setOpenAIQModel}
                    selectedRow={selectedRow}
                    reloadDataTable={reloadDataTable}
                />
            )}

            <div>
                {showFilters && (
                    <>
                        <div className="pageHeader">
                            <span id="header">
                                <h4>Lab orders</h4>
                            </span>
                        </div>
                        <hr />

                        {message && <MessageContainer message={message} />}
                        <form
                            className="row g-2"
                            onSubmit={handleSubmit(setSearchFilters)}>
                            <div className="col-md-2">
                                <label htmlFor="search">
                                    Search by sample :
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="search"
                                    placeholder="Enter sample id"
                                    {...register('searchText')}
                                />
                            </div>
                            <div className="col-md-2">
                                <label htmlFor="start_date">Status:</label>
                                <select
                                    className="form-control"
                                    id="Status"
                                    {...register('currentStatus')}>
                                    <option value="" key="0">
                                        All
                                    </option>
                                    {Object.keys(labOrderStatus).map(status => (
                                        <option
                                            value={labOrderStatus[status]}
                                            key={status}>
                                            {labOrderStatus[status]}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-2">
                                <div className="row">
                                    <label htmlFor="start_date">
                                        Start Date :
                                    </label>
                                    <div>
                                        <Controller
                                            control={control}
                                            id="start_date"
                                            name="start_date"
                                            render={({ field }) => (
                                                <DatePicker
                                                    selected={field.value}
                                                    minDate={
                                                        new Date(
                                                            dates.START_DATE,
                                                        )
                                                    }
                                                    maxDate={new Date()}
                                                    onChange={e => {
                                                        if (!e) {
                                                            setStartDate('');
                                                            setValue(
                                                                'start_date',
                                                                '',
                                                            );
                                                        } else {
                                                            setStartDate(
                                                                customizeDateFormat(
                                                                    e,
                                                                    'YYYY-MM-DD HH:mm',
                                                                ),
                                                            );
                                                            setValue(
                                                                'start_date',
                                                                e,
                                                            );
                                                            if (
                                                                getValues(
                                                                    'start_date',
                                                                ) >
                                                                getValues(
                                                                    'end_date',
                                                                )
                                                            ) {
                                                                setValue(
                                                                    'end_date',
                                                                    null,
                                                                );
                                                                setEndDate('');
                                                            }
                                                        }
                                                    }}
                                                    placeholderText="Select start date"
                                                    dateFormat="MMMM d, yyyy"
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="row">
                                    <div>
                                        <label htmlFor="end_date">
                                            End Date :
                                        </label>
                                    </div>
                                    <div>
                                        <Controller
                                            control={control}
                                            id="end_date"
                                            name="end_date"
                                            render={({ field }) => (
                                                <DatePicker
                                                    selected={field.value}
                                                    onChange={e => {
                                                        if (!e) {
                                                            setEndDate('');
                                                            setValue(
                                                                'end_date',
                                                                '',
                                                            );
                                                        } else {
                                                            var newEndDate =
                                                                new Date(
                                                                    customizeDateFormat(
                                                                        e,
                                                                        'YYYY-MM-DD HH:mm',
                                                                    ),
                                                                );
                                                            newEndDate.setHours(
                                                                23,
                                                            );
                                                            newEndDate.setMinutes(
                                                                59,
                                                            );
                                                            setEndDate(
                                                                customizeDateFormat(
                                                                    newEndDate,
                                                                    'YYYY-MM-DD HH:mm',
                                                                ),
                                                            );
                                                            setValue(
                                                                'end_date',
                                                                e,
                                                            );
                                                        }
                                                    }}
                                                    placeholderText="Select end date"
                                                    maxDate={new Date()}
                                                    minDate={getValues(
                                                        'start_date',
                                                    )}
                                                    dateFormat="MMMM d, yyyy"
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div>&nbsp;</div>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}>
                                    {loading && (
                                        <span className="spinner-border spinner-border-sm"></span>
                                    )}
                                    Search
                                </button>
                                <span className="mx-1"></span>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}
                                    onClick={() => {
                                        setStartDate('');
                                        setEndDate('');
                                        reset();
                                    }}>
                                    Reset
                                </button>
                                <span className="mx-1"></span>
                                <button
                                    disabled={loading}
                                    className="btn btn-primary"
                                    onClick={e => setOpenExportForm(true)}>
                                    <ExcelIcon />
                                    &nbsp; Export
                                </button>
                            </div>
                        </form>
                    </>
                )}
                <span
                    color="primary"
                    className="float-end"
                    style={{ marginRight: '10px' }}
                    onClick={() => reloadDataTable()}>
                    <SyncIcon />
                </span>
                <div className="new-line"></div>
                <div className="accounts-list-table">
                    <DataTable
                        width="100%"
                        customStyles={
                            props.accountId
                                ? customTableStylesAccountView
                                : customTableStyles
                        }
                        columns={columns}
                        data={data}
                        progressPending={tableLoading}
                        paginationPerPage={perPage}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onChangePage={handlePageChange}
                        conditionalRowStyles={conditionalRowStyles}
                        expandableRows
                        expandableRowsComponent={ExpandedComponent}
                    />
                </div>
            </div>
        </>
    );
};

const ExpandedComponent = ({ data }) => {
    const columns = [
        {
            name: 'Current Result',
            expander: true,
            selector: row => showPhenoTypes(row.current_pheno_types),
        },
    ];
    return (
        <div style={{ width: '100%', marginLeft: '3.3%' }}>
            <DataTable
                customStyles={{
                    headCells: {
                        style: {
                            backgroundColor: '#fafafa',
                            fontSize: '13px',
                        },
                    },
                }}
                width="100%"
                columns={columns}
                data={[data]}
            />
        </div>
    );
};

export default WithLogin(LabOrderList);
